import {
  AuthorizationNotifier,
  AuthorizationRequest,
  AuthorizationServiceConfiguration,
  BaseTokenRequestHandler,
  DefaultCrypto,
  FetchRequestor,
  GRANT_TYPE_AUTHORIZATION_CODE,
  LocalStorageBackend,
  RedirectRequestHandler,
  TokenRequest,
} from '@openid/appauth';
import { getFullJwt } from './getFullJwt';
import { getPartialJwt } from './getPartialJwt';
import { NoHashQueryStringUtils } from './noHashQueryStringUtils';

export default class OIDCAuthManager {
  constructor(config) {
    this.config = config;
    this.notifier = new AuthorizationNotifier();
    this.tokenHandler = new BaseTokenRequestHandler(new FetchRequestor());
    this.authorizationHandler = new RedirectRequestHandler(
      new LocalStorageBackend(),
      new NoHashQueryStringUtils(),
      window.location,
      new DefaultCrypto(),
    );
    // set notifier to deliver responses
    this.authorizationHandler.setAuthorizationNotifier(this.notifier);
  }

  async getAuthServiceConfiguration() {
    // Fetches auth service configuration, if it doesn't yet exist
    if (!this.authServiceConfiguration) {
      const response = await AuthorizationServiceConfiguration.fetchFromIssuer(
        this.config.authority,
        new FetchRequestor(),
      );

      this.authServiceConfiguration = response;
    }
  }

  async performStepUpAuthentication(locale, formName, clientNumber, certificate) {
    const extras = {
      ui_locales: locale,
      claims: JSON.stringify({
        id_token: { acr: { essential: true, values: ['stepup_conditional'] }, ui_locales: { value: locale } },
      }),
    };

    // add language, formName, certificate, and clientNumber in the OIDC state
    const UUID = crypto.randomUUID();
    const state = `gb_${btoa(
      `${UUID}&clientNumber=${clientNumber}&certificate=${certificate}&formName=${formName}&lang=${locale}`,
    )}`;

    // Create a request to step up at identity provider
    const authRequest = new AuthorizationRequest({
      client_id: this.config.client_id,
      redirect_uri: this.config.redirect_uri,
      scope: this.config.scope,
      response_type: AuthorizationRequest.RESPONSE_TYPE_CODE,
      state,
      extras,
    });

    try {
      await this.getAuthServiceConfiguration();

      // Redirect to the identity provider login using the provided configuration
      this.authorizationHandler.performAuthorizationRequest(this.authServiceConfiguration, authRequest);
    } catch (err) {
      throw new Error('Ooops! Something went wrong when performing auth request');
    }
  }

  async stepUpCallback(responseHandler, transactionId) {
    this.notifier.setAuthorizationListener(this.createAuthorizationListener(responseHandler, transactionId));

    // Start the processing of identity provider response (callback)
    return this.authorizationHandler.completeAuthorizationRequestIfPossible();
  }

  createAuthorizationListener(responseHandler, transactionId) {
    // This listener is created to do the following:
    // - listen for authorization responses
    // - make id, refresh, and access token requests (using auth response)
    // - calls responseHandler callback function which handles the redirect after successful step up

    return async (request, response, error) => {
      if (response && !error) {
        try {
          // Get code verifier (PKCE) from authorization request to enable validation of auth code
          const codeVerifier = request?.internal?.code_verifier;

          // Request for tokens: id token, access token, and refresh token - using the auth code
          const tokenResponse = await this.makeTokenRequest(response.code, codeVerifier);

          // get language, formName, client number, and certificate from OIDC state
          const decodedState = atob(response.state.split('gb_')[1]);
          const stateParams = new URLSearchParams(decodedState);
          const formName = stateParams.get('formName');
          const clientNumber = stateParams.get('clientNumber');
          const certificate = stateParams.get('certificate');
          const lang = stateParams.get('lang');

          const partialJWT = await getPartialJwt(tokenResponse.idToken, transactionId);
          const fullJWT = await getFullJwt(partialJWT, clientNumber, certificate, transactionId);
          const jwt = fullJWT['gb-authorization'];
          const sessionCorrelationId = fullJWT['mli-session-correlation-id'];

          // Call response handler. This function updates redux state and redirects to return path.
          return responseHandler(jwt, sessionCorrelationId, formName, lang);
          // TODO: Update response handler for Step 3
        } catch (err) {
          // Error making token request from Auth Service or BFF
          return responseHandler();
        }
      }

      // If there is an error value or there is no response
      return responseHandler();
    };
  }

  async makeTokenRequest(code, codeVerifier) {
    const extras = { code_verifier: codeVerifier };

    // Create a request for tokens (access token, id token, refresh token)
    const tokenRequest = new TokenRequest({
      client_id: this.config.client_id,
      redirect_uri: this.config.redirect_uri,
      grant_type: GRANT_TYPE_AUTHORIZATION_CODE,
      refresh_token: null,
      code,
      extras,
    });

    await this.getAuthServiceConfiguration();

    // Request for tokens (id token, access token, and refresh token) using the provided configuration
    const tokenResponse = await this.tokenHandler.performTokenRequest(this.authServiceConfiguration, tokenRequest);

    return tokenResponse;
  }
}
